import {
  GeneralModalProps,
  RootState,
} from "../../assets/interfaces/interfaces";
import {
  Modal,
  IconButton,
  Box,
  Button,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { shareTransform } from "../../helpers";
import ChatMessage from "../ChatMessage";
import { AppDispatch } from "../../redux/store";
import { fetchSharedSession } from "../../redux/slices/sharedSessionSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import { fetchChangeTitle } from "../../redux/slices/changeTitleSlice";
import useWindowDimensions from "../../hooks/useWindowDimensions";

const SharePreviewModal: React.FC<GeneralModalProps> = ({
  open,
  handleClose,
  session_id = "",
}) => {
  const [messages, setMessages] = useState<any[]>([]);

  const theme = useTheme();

  const isDarkMode = useSelector((state: any) => state?.theme?.darkMode as any);
  const user = useSelector((state: any) => state.auth.currentUser);

  const dispatch = useDispatch<AppDispatch>(); // Use the typed dispatch

  // This is added just to add some margins on mobile version :)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { width } = useWindowDimensions();

  const style: SxProps<Theme> = {
    position: "absolute",
    top: isMobile ? "50%" : "45%",
    left: "50%",
    width: isMobile ? width - 20 : 550,
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    boxShadow: 24,
  };

  const {
    pastMessages,
    // status
  } = useSelector((state: RootState) => state.sharedSession);

  useEffect(() => {
    if (open) {
      dispatch(fetchSharedSession({ sessionId: session_id }));
    }
  }, [session_id, dispatch, open]);

  useEffect(() => {
    if (pastMessages.length) {
      const formattedPastChat = shareTransform(pastMessages);
      setMessages(formattedPastChat);
    }
  }, [pastMessages, user]);
  const copyToClipboard = () => {
    const domain = window.location.origin;
    const linkToCopy = `${domain}/share/${session_id}`;
    navigator.clipboard.writeText(linkToCopy);
    dispatch(
      showNotification({
        message: "Link copied to clipboard!",
        severity: "success",
        horizontal: "right",
      })
    );
    handleClose();
  };
  const sessionTitle = useMemo(() => {
    if (messages[0] && messages[0]?.title) {
      return messages[0]?.title;
    }
    return "";
  }, [messages]);
  const session_date = useMemo(() => {
    if (!!messages[0] && messages[0]?.date) {
      // Remove quotes from the title
      return messages[0].date;
    }
    return "";
  }, [messages]);
  const [title, setTitle] = useState("");
  const [rename, setRename] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null); // Specify the ref type as HTMLInputElement
  // Set focus to the input when renaming
  useEffect(() => {
    if (rename && renameInputRef.current) {
      renameInputRef?.current.focus();
      renameInputRef?.current.select();
    }
  }, [rename, renameInputRef]);
  const changeTitleReq = () => {
    dispatch(
      fetchChangeTitle({
        session_id,
        title,
        userId: user?.userId,
        fetchSession: true,
      }) as any
    );
  };
  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      changeTitleReq();
      setRename(false); // Set rename to false when Enter is pressed
      // Additional actions can be added here if needed
    }
    if (e.key === "Escape") {
      setRename(false);
    }
  };
  const finaltitle =  (title || sessionTitle).replace(/['"]/g, '');
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={style}
        className="ModalShareAnoymously flex flex-col w-full mx:mx-0 md:w-[550px] md:h-[610px] rounded-[10px] shadow bg-white"
      >
        {/* Modal header */}
        <div className="Header p-6 border-b border-zinc-200 items-center w-full flex">
          {/* Header title */}
          <div className="LeftInfo flex-1">
            <div className="Share-Text grow shrink basis-0 text-zinc-900 text-lg font-bold font-['Inter'] leading-normal">
              Share link to Chat
            </div>
          </div>

          {/* Header close button */}
          <IconButton onClick={handleClose} className="w-2.5 h-2.5">
            <CloseIcon sx={{ fill: isDarkMode ? "white" : "" }} />
          </IconButton>
        </div>

        {/* Modal content */}
        <div className="Content p-6 flex-col gap-4 inline-flex">
          <div className="Body rounded-lg flex-col gap-6 flex">
            {/* Subtitle */}
            <div className="MessagesYouSendAfterCreatingYourLinkWonTBeSharedAnyoneWithTheUrlWillBeAbleToViewTheSharedChat  text-neutral-400 text-base font-normal font-['Inter'] leading-snug">
              Messages you send after creating your link won’t be shared. Anyone
              with the URL will be able to view the shared chat.
            </div>

            {/* Message */}
            <div className="Content-shared bg-white rounded-lg shadow border border-zinc-200 flex-col justify-start items-start flex">
              <div className="chatmessagescrollbar w-full justify-start items-start inline-flex">
                <div className="ChatMessage scroll-container overflow-y-scroll h-[264px] p-2 flex-col gap-2">
                  {messages.map((message: any, index: any) => (
                    <ChatMessage message={message} />
                  ))}
                </div>
              </div>

              {/* Share footer */}
              <div className="BottomSection w-full p-4 border-t border-zinc-200 items-center flex">
                {/* Footer - Left side */}
                <div className="Info flex-col flex-1 gap-0.5 flex">
                  {/* Title and edit button */}
                  <div className="Title items-center gap-2 flex">
                    {rename ? (
                      <input
                        onKeyDown={handleKeyPress}
                        ref={renameInputRef}
                        className="sidebar-input w-[250px] text-white border border-blue-500 bg-transparent p-0 text-sm"
                        placeholder={finaltitle}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    ) : (
                      <div className="CompareTslaWithTheSP500 whitespace-nowrap h-6 text-zinc-900 text-base font-normal font-['Inter'] leading-snug">
                        {finaltitle || "No data"}
                      </div>
                    )}
                    {!rename && (finaltitle) && (
                      <IconButton
                        onClick={() => setRename(true)}
                        sx={{ position: "relative", top: "-3px" }}
                      >
                        <ModeEditIcon sx={{ zIndex: 10, fill: "grey" }} />
                      </IconButton>
                    )}
                  </div>

                  {/* Session date */}
                  <div className="January2024 truncate max-w-[424px] h-6 text-neutral-400 text-base font-normal font-['Inter'] leading-snug">
                    {session_date || "No date data"}
                  </div>
                </div>

                {/* Footer - Right side */}
                <IconButton>
                  <MoreHorizIcon sx={{ zIndex: 10, fill: "grey" }} />
                </IconButton>
              </div>
            </div>
          </div>

          <Button
            onClick={copyToClipboard}
            variant="contained"
            type="button"
            className={`!rounded-md !w-[106px] !h-[44px] self-end !text-white !font-inter !text-[13px] !normal-case !font-bold !leading-[18px] !tracking-tight !p-[16px] whitespace-nowrap !bg-black text-white`}
          >
            Copy Link
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default SharePreviewModal;
